/**
 * Where the Medium tier's fixtures go (lifted from the placement half of the
 * dungeon-floors prototype's `dungeon-lighting.js`): the doors-and-turns torch
 * rule with room back-wall fixtures, floor braziers before sconces, rail-clear
 * sites, hanging lanterns on frame headers, shafts into large rooms and the
 * grade zones. The marker lights and the bake's light list are in
 * `light-sources.ts`. Pure: seeded by the floor's seed, never by the
 * simulation; nothing here blocks a tile.
 */
import type { Theme } from '@ab/content-schema';
import type { Floor, Room, Unit } from '@ab/game-core';
import * as THREE from 'three';
import { presentation } from './constants.ts';
import { isOpen, orthogonalSteps, roomAt, seededRandom, tileIndex } from './grid.ts';
import { type MarkerLight, bakeSources, markerLights } from './light-sources.ts';
import type { LightSource } from './radiance.ts';

export type Bounds = { readonly x0: number; readonly x1: number; readonly z0: number; readonly z1: number; readonly y0: number; readonly y1: number };
export type Track = { readonly tile: number; readonly neighbors: readonly number[] };
export type FrameSite = { readonly kind: 'portal' | 'room-frame'; readonly x: number; readonly y: number; readonly axis: 'x' | 'z'; readonly height: number };
export type Fixture = { tileX: number; tileY: number; tile: number; x: number; z: number; readonly color: THREE.Color; mount: 'floor' | 'wall'; normal: readonly [number, number]; kind: string; readonly radius?: number; readonly gain?: number };
export type Lantern = { readonly x: number; readonly z: number; readonly tile: number; readonly height: number; readonly top: number; readonly color: THREE.Color };
export type ShaftPlan = { readonly top: readonly [number, number, number]; readonly base: readonly [number, number, number]; readonly at: readonly [number, number]; readonly width: number };
export type Zone = { readonly rect?: readonly [number, number, number, number]; readonly circle?: readonly [number, number, number]; readonly feather: number; readonly temp: number; readonly light: number; readonly air: number };

export type LightingPlan = {
  readonly torches: readonly Fixture[];
  readonly lanterns: readonly Lantern[];
  readonly markerLights: readonly MarkerLight[];
  readonly shafts: readonly ShaftPlan[];
  readonly zones: readonly Zone[];
  /** Everything the bake reads (`radiance.ts`). */
  readonly sources: readonly LightSource[];
};

export type PlanInput = {
  readonly floor: Floor;
  readonly units: readonly Unit[];
  readonly seed: number;
  readonly theme: Theme;
  readonly tracks: readonly Track[];
  readonly frames: readonly FrameSite[];
  /** Standing props a fixture must not cut into. */
  readonly obstacles: readonly Bounds[];
  /** Medium wall height at a tile (a cutaway stub counts as 0: sconces hang only on full faces). */
  readonly wallHeight: (x: number, y: number) => number;
  /** Rock top at a world point, or null. */
  readonly topAt: (x: number, z: number) => number | null;
};

const f = presentation.medium.fixtures;
export type Dir = readonly [number, number];
const dirs: readonly Dir[] = orthogonalSteps;
export type Candidate = { x: number; y: number; wall: Dir; room: number; priority: number; rank: number; kind?: string; radius?: number; gain?: number; mount?: 'wall' };

/** Rail centre lines as dense samples: straight halves to each shared edge, quarter circles on bends. */
const railCentreline = (floor: Floor, tracks: readonly Track[]): [number, number][] => {
  const pts: [number, number][] = [];
  for (const t of tracks) {
    const x = t.tile % floor.width;
    const y = Math.floor(t.tile / floor.width);
    const ds = t.neighbors.map((n): Dir => [(n % floor.width) - x, Math.floor(n / floor.width) - y]);
    const [a, b] = ds;
    if (ds.length === 2 && a !== undefined && b !== undefined && a[0] !== -b[0] && a[1] !== -b[1]) {
      const cx = x + (a[0] || b[0]) * 0.5;
      const cy = y + (a[1] || b[1]) * 0.5;
      const a0 = Math.atan2(y + a[1] * 0.5 - cy, x + a[0] * 0.5 - cx);
      let da = Math.atan2(y + b[1] * 0.5 - cy, x + b[0] * 0.5 - cx) - a0;
      if (da > Math.PI) da -= Math.PI * 2;
      if (da < -Math.PI) da += Math.PI * 2;
      for (let s = 0; s <= 8; s += 1) pts.push([cx + Math.cos(a0 + (da * s) / 8) * 0.5, cy + Math.sin(a0 + (da * s) / 8) * 0.5]);
    } else for (const [dx, dy] of ds) for (let s = 0; s <= 5; s += 1) pts.push([x + (dx * 0.5 * s) / 5, y + (dy * 0.5 * s) / 5]);
    if (ds.length === 0) pts.push([x, y]);
  }
  return pts;
};

/** Back-row cells of a room with rock behind them, nearest the centre column first (ties west first); shared with the dressing reservations. */
export const backWallCells = (floor: Floor, r: Room): number[] => {
  const y = r.y + r.h - 1;
  const cx = r.x + (r.w - 1) / 2;
  const list: number[] = [];
  for (let x = r.x; x < r.x + r.w; x += 1) if (!isOpen(floor, x, y + 1)) list.push(x);
  return list.sort((a, b) => Math.abs(a + f.site[0] - cx) - Math.abs(b + f.site[0] - cx) || a - b);
};

export const planLighting = (input: PlanInput): LightingPlan => {
  const { floor, units, theme, obstacles, wallHeight } = input;
  const rules = theme.lightRules;
  const pct = (v: number): number => v / 100;
  const random = seededRandom(input.seed || 1);
  const open = (x: number, y: number): boolean => isOpen(floor, x, y);
  const room = (x: number, y: number): number => roomAt(floor, x, y);
  const warm = new THREE.Color(rules.warm);
  const bossColor = new THREE.Color(theme.bossRoom.accent);
  const hasBoss = units.some((u) => u.boss !== null);
  const tint = (ri: number): THREE.Color => (ri === floor.exitRoom && hasBoss ? warm.clone().lerp(bossColor, f.bossMix) : warm.clone());
  const markers = markerLights(floor, units, theme);
  const lit = markers.filter((t) => t.kind !== 'spring');

  const candidates: Candidate[] = [];
  const byWallSide = [...dirs].sort((a, b) => Math.abs(b[0]) - Math.abs(a[0]));
  for (let y = 1; y < floor.height - 1; y += 1) {
    for (let x = 1; x < floor.width - 1; x += 1) {
      if (!open(x, y)) continue;
      const openDirs = dirs.filter(([dx, dy]) => open(x + dx, y + dy));
      const wall = byWallSide.find(([dx, dy]) => !open(x + dx, y + dy));
      if (wall === undefined) continue;
      const r = room(x, y);
      const door = openDirs.some(([dx, dy]) => r >= 0 !== room(x + dx, y + dy) >= 0);
      const [o0, o1] = openDirs;
      const turn = r < 0 && openDirs.length === 2 && o0 !== undefined && o1 !== undefined && o0[0] !== -o1[0];
      if (door || turn) candidates.push({ x, y, wall, room: r, priority: turn ? -2 : 0, rank: random() });
    }
  }
  // Every room gets a boundary fixture against its back wall, nearest its centre column.
  const backSites = floor.rooms.map((r, ri) => {
    random();
    random();
    const list = backWallCells(floor, r).map((x): Candidate => ({ x, y: r.y + r.h - 1, wall: [0, 1], room: ri, priority: 2, rank: 0 }));
    const first = list[0];
    if (first !== undefined) candidates.push(first);
    return list;
  });
  candidates.sort((a, b) => a.priority - b.priority || a.rank - b.rank);

  const rail = railCentreline(floor, input.tracks);
  const railClear = pct(rules.torchRailClearancePercent);
  const onRail = (x: number, y: number): boolean => railClear > 0 && rail.some(([u, v]) => Math.hypot(u - x, v - y) < railClear || (Math.abs(u - x) < f.railLane[0] && v > y && v - y < f.railLane[1]));
  const siteX = (c: { x: number; wall: Dir }): number => c.x + (c.wall[0] || 1) * f.site[0];
  const siteY = (c: { y: number; wall: Dir }): number => c.y + c.wall[1] * f.site[1];
  const occupied = new Set([...units, ...floor.items, ...floor.springs].map((v) => tileIndex(floor, v.pos.x, v.pos.y)));
  floor.tiles.forEach((t, i) => {
    if (t === 'exit') occupied.add(i);
  });
  const blocked = (x: number, y: number): boolean => obstacles.some((b) => b.x0 < x + f.clear && b.x1 > x - f.clear && b.z0 < -y + f.clear && b.z1 > -y - f.clear);
  const sconceBlocked = (c: Candidate): boolean => {
    const x = c.x + c.wall[0] * f.sconce;
    const y = c.y + c.wall[1] * f.sconce;
    const h = f.sconceClear;
    return obstacles.some((b) => b.y0 < f.sconceBand[1] && b.y1 > f.sconceBand[0] && b.x0 < x + h && b.x1 > x - h && b.z0 < -y + h && b.z1 > -y - h);
  };
  const legalSlide = (c: Candidate, n: Candidate): boolean => open(n.x, n.y) && !open(n.x + c.wall[0], n.y + c.wall[1]) && !occupied.has(tileIndex(floor, n.x, n.y)) && room(n.x, n.y) === c.room;
  const clearSite = (c: Candidate): Candidate | null => {
    if (!onRail(siteX(c), siteY(c)) && (c.priority !== 2 || !blocked(siteX(c), siteY(c)))) return c;
    const along: Dir = c.wall[0] !== 0 ? [0, 1] : [1, 0];
    for (const t of [1, -1, 2, -2]) {
      const n = { ...c, x: c.x + along[0] * t, y: c.y + along[1] * t };
      if (legalSlide(c, n) && !onRail(siteX(n), siteY(n)) && !blocked(siteX(n), siteY(n))) return n;
    }
    // No free floor spot: an iron sconce on a full-height face; camera-side stubs are too low.
    if (c.wall[1] === -1) return null;
    for (const t of [0, 1, -1, 2, -2]) {
      const n: Candidate = { ...c, x: c.x + along[0] * t, y: c.y + along[1] * t, mount: 'wall' };
      if (t !== 0 && !legalSlide(c, n)) continue;
      if (wallHeight(n.x + c.wall[0], n.y + c.wall[1]) >= f.sconceMinWall && !sconceBlocked(n)) return n;
    }
    return null;
  };
  const torches: Fixture[] = [];
  const spacing = pct(rules.spacingPercent);
  for (const candidate of candidates) {
    if (candidate.priority >= 0 && candidate.priority < 2 && candidate.rank > pct(rules.torchDensityPercent)) continue;
    const c = clearSite(candidate);
    if (c === null) continue;
    if (lit.some((t) => Math.hypot(t.x - c.x, t.z + c.y) < f.markerClear)) continue;
    if (torches.some((t) => Math.hypot(t.tileX - c.x, t.tileY - c.y) < (c.priority < 0 ? f.prioritySpacing : spacing))) continue;
    const wall = c.mount === 'wall';
    torches.push({
      tileX: c.x,
      tileY: c.y,
      tile: tileIndex(floor, c.x, c.y),
      x: wall ? c.x + c.wall[0] * f.sconce : siteX(c),
      z: wall ? -c.y - c.wall[1] * f.sconce : -siteY(c),
      color: tint(c.room),
      mount: wall ? 'wall' : 'floor',
      normal: [-c.wall[0], c.wall[1]],
      kind: c.priority === 0 ? 'door' : c.priority < 0 ? 'turn' : 'room-boundary',
    });
  }
  // One visible flame per room: a room whose fixtures all sit off its central columns takes a back-wall site.
  const v = f.roomView;
  const inView = (t: { x: number; z: number }, r: Room): boolean =>
    t.x >= r.x - v.margin && t.x <= r.x + r.w - v.back && -t.z >= r.y + r.h * v.northShare && -t.z <= r.y + r.h + v.margin && Math.abs(t.x - (r.x + (r.w - 1) / 2)) <= Math.max(v.minHalf, r.w / 2 - v.inset);
  floor.rooms.forEach((r, ri) => {
    if ([...torches, ...lit].some((t) => inView(t, r))) return;
    for (const c0 of backSites[ri] ?? []) {
      if (occupied.has(tileIndex(floor, c0.x, c0.y))) continue;
      let c: Candidate | null = null;
      if (!onRail(siteX(c0), siteY(c0)) && !blocked(siteX(c0), siteY(c0))) c = c0;
      else if (wallHeight(c0.x, c0.y + 1) >= f.sconceMinWall && !sconceBlocked({ ...c0, mount: 'wall' })) c = { ...c0, mount: 'wall' };
      if (c === null) continue;
      const wall = c.mount === 'wall';
      const x = wall ? c.x : siteX(c);
      const z = wall ? -c.y - f.sconce : -siteY(c);
      if (torches.some((t) => Math.hypot(t.x - x, t.z - z) < f.relaxedSpacing)) continue;
      torches.push({ tileX: c.x, tileY: c.y, tile: tileIndex(floor, c.x, c.y), x, z, color: tint(ri), mount: wall ? 'wall' : 'floor', normal: [0, 1], kind: 'room-boundary' });
      return;
    }
  });
  // A room still without a fixture takes one at a back corner, the one farther from other fixtures.
  floor.rooms.forEach((r, ri) => {
    if (torches.some((t) => room(t.tileX, t.tileY) === ri)) return;
    const nearest = (x: number, y: number): number => Math.min(f.far, ...torches.map((t) => Math.hypot(t.x - x, t.z + y)));
    const corners: (readonly [number, number, number])[] = [
      [r.x, r.y + r.h - 1, -1],
      [r.x + r.w - 1, r.y + r.h - 1, 1],
    ];
    corners.sort((a, b) => nearest(b[0], b[1]) - nearest(a[0], a[1]) || a[0] - b[0]);
    const pick = corners.find(([cx, cy, cs]) => !onRail(cx + cs * f.site[1], cy));
    if (pick === undefined) return;
    const [x, y, side] = pick;
    if ([...torches, ...markers].some((t) => Math.hypot(t.x - x, t.z + y) < spacing)) return;
    torches.push({ tileX: x, tileY: y, tile: tileIndex(floor, x, y), x: x + side * f.site[1], z: -y, color: warm.clone(), mount: 'floor', normal: [0, 1], kind: 'room-boundary' });
  });
  // Floor braziers first, sconces second (lightRules.brazierReach).
  const brazierReach = pct(rules.brazierReachPercent);
  if (brazierReach > 0) {
    const lights = (): { x: number; z: number; mount?: string; tileX?: number; tileY?: number }[] => [...torches, ...lit];
    const floorSite = (x: number, y: number, wall: Dir, ri: number): Candidate | null => {
      const c: Candidate = { x, y, wall, room: ri, priority: 0, rank: 0 };
      if (!open(x, y) || open(x + wall[0], y + wall[1]) || occupied.has(tileIndex(floor, x, y)) || room(x, y) !== ri) return null;
      return onRail(siteX(c), siteY(c)) || blocked(siteX(c), siteY(c)) ? null : c;
    };
    const place = (t: Fixture, c: Candidate, kind: string): Fixture => Object.assign(t, { tileX: c.x, tileY: c.y, tile: tileIndex(floor, c.x, c.y), x: siteX(c), z: -siteY(c), mount: 'floor' as const, normal: [-c.wall[0], c.wall[1]] as const, kind });
    const clearOf = (c: Candidate, self: object | null): boolean => !lights().some((t) => t !== self && Math.hypot(t.x - siteX(c), t.z + siteY(c)) < f.relaxedSpacing);
    const notCameraSide = dirs.filter((w) => w[1] !== -1);
    for (const t of torches.filter((s) => s.mount === 'wall')) {
      const ri = room(t.tileX, t.tileY);
      const sites: Candidate[] = [];
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          for (const wall of notCameraSide) {
            const c = floorSite(t.tileX + dx, t.tileY + dy, wall, ri);
            if (c !== null && Math.hypot(siteX(c) - t.x, siteY(c) + t.z) <= f.sconceToFloor) sites.push(c);
          }
        }
      }
      sites.sort((a, b) => Math.hypot(siteX(a) - t.x, siteY(a) + t.z) - Math.hypot(siteX(b) - t.x, siteY(b) + t.z) || a.y - b.y || a.x - b.x);
      const c = sites.find((s) => clearOf(s, t));
      if (c !== undefined) place(t, c, t.kind);
    }
    floor.rooms.forEach((r, ri) => {
      if (torches.some((t) => t.mount !== 'wall' && room(t.tileX, t.tileY) === ri)) return;
      const cx = r.x + (r.w - 1) / 2;
      const sites: Candidate[] = [];
      for (let y = r.y; y < r.y + r.h; y += 1) {
        for (let x = r.x; x < r.x + r.w; x += 1) {
          for (const wall of notCameraSide) {
            const c = floorSite(x, y, wall, ri);
            if (c !== null && clearOf(c, null)) sites.push(c);
          }
        }
      }
      const rank = (c: Candidate): number => Math.abs(siteX(c) - cx) + Math.max(0, r.y + r.h * 0.5 - c.y) * f.backWeight;
      sites.sort((a, b) => rank(a) - rank(b) || b.y - a.y || a.x - b.x);
      const best = sites[0];
      if (best !== undefined) torches.push(place({ tileX: 0, tileY: 0, tile: 0, x: 0, z: 0, color: tint(ri), mount: 'floor', normal: [0, 1], kind: '' }, best, 'room-brazier'));
    });
    for (const cand of candidates.filter((c) => c.priority <= 0 && c.kind === undefined)) {
      if (lights().some((t) => Math.hypot(t.x - cand.x, t.z + cand.y) <= brazierReach)) continue;
      const along: Dir = cand.wall[0] !== 0 ? [0, 1] : [1, 0];
      let site: Candidate | null = null;
      for (const s of [0, 1, -1, 2, -2]) {
        const c = floorSite(cand.x + along[0] * s, cand.y + along[1] * s, cand.wall, cand.room);
        if (c !== null && clearOf(c, null)) {
          site = c;
          break;
        }
      }
      if (site !== null) torches.push(place({ tileX: 0, tileY: 0, tile: 0, x: 0, z: 0, color: tint(cand.room), mount: 'floor', normal: [0, 1], kind: '' }, site, 'junction'));
    }
  }
  // Hanging lanterns on a few corridor portals and room headers, beside a post, never over the walked line.
  const lanterns: Lantern[] = [];
  const l = f.lantern;
  const seedOf = (x: number, y: number): number => {
    let n = (input.seed | 0) ^ Math.imul(Math.round(x * 2) + 71, 374761393) ^ Math.imul(Math.round(y * 2) + 137, 668265263);
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
  };
  for (const fr of [...input.frames].sort((a, b) => seedOf(a.x, a.y) - seedOf(b.x, b.y) || a.x - b.x || a.y - b.y)) {
    if (lanterns.length >= rules.lanternMax || seedOf(fr.x, fr.y + 0.5) > pct(rules.lanternDensityPercent)) continue;
    const side = fr.axis === 'x' ? (seedOf(fr.x + 0.5, fr.y) < 0.5 ? -1 : 1) : -1;
    const header = fr.kind === 'room-frame' ? floor.rooms.find((r) => Math.abs(r.x + (r.w - 1) / 2 - fr.x) < 0.01 && Math.abs(r.y + r.h - 1 + presentation.props.roomFrameOffset - fr.y) < 0.01) : undefined;
    if (fr.kind === 'room-frame' && (header === undefined || header.w < l.minRoomWidth)) continue;
    const along = header !== undefined ? header.w / 2 - l.roomInset : l.portalAlong;
    const x = fr.axis === 'x' ? fr.x + side * along : fr.x;
    const z = fr.axis === 'x' ? -fr.y + l.face : -fr.y + side * l.side;
    if ([...torches, ...lanterns].some((t) => Math.hypot(t.x - x, t.z - z) < l.spacing)) continue;
    lanterns.push({ x, z, tile: tileIndex(floor, Math.round(x), Math.round(fr.y)), height: fr.height - l.drop, top: fr.height - l.hang, color: warm.clone() });
  }
  // Shafts: from a real back-wall rock top down into the largest rooms.
  const s = theme.shafts;
  const shafts: ShaftPlan[] = [];
  const toSun = new THREE.Vector3(...f.shaftDirection).normalize();
  for (const r of floor.rooms.filter((q) => q.w * q.h >= s.minRoomArea).sort((a, b) => b.w * b.h - a.w * a.h || a.x - b.x || a.y - b.y).slice(0, s.maxPerFloor)) {
    const x = r.x + f.shaftInset + random() * Math.max(0, r.w - f.shaftSpan);
    const z = -r.y - r.h + f.shaftBack;
    const height = input.topAt(x, z);
    if (height === null || height <= 0) continue;
    const length = height / toSun.y;
    shafts.push({ top: [x, height, z], base: [x - toSun.x * length, 0, z - toSun.z * length], at: [x - toSun.x * length, z - toSun.z * length], width: pct(s.widthPercent) });
  }
  // Grade zones: the whole mine in shade, warm circles at every fixture, and the theme's own.
  const z = f.zones;
  const zones: Zone[] = [
    { rect: [-0.5, -floor.height + 0.5, floor.width - 0.5, 0.5], feather: z.shadeFeather, temp: rules.zoneShadeTempPermille / 1000, light: rules.zoneShadeLightPermille / 1000, air: 0 },
    ...[...torches, ...lanterns].map((t): Zone => ({ circle: [t.x, t.z, 'mount' in t ? z.torchRadius : z.lanternRadius], feather: z.warmFeather, temp: rules.zoneWarmTempPermille / 1000, light: rules.zoneWarmLightPermille / 1000, air: 0 })),
    ...theme.zones.map((q): Zone => ({
      ...(q.rect !== undefined ? { rect: [q.rect[0] - 0.5, -q.rect[3] - 0.5, q.rect[2] + 0.5, -q.rect[1] + 0.5] as const } : {}),
      ...(q.circle !== undefined ? { circle: [q.circle[0], -q.circle[1], pct(q.radiusPercent ?? 0)] as const } : {}),
      feather: pct(q.featherPercent),
      temp: q.tempPermille / 1000,
      light: q.lightPermille / 1000,
      air: q.airPermille / 1000,
    })),
  ];
  return { torches, lanterns, markerLights: markers, shafts, zones, sources: bakeSources(input, torches, lanterns, markers, shafts, candidates) };
};
